/* eslint-disable camelcase */
import { FC, useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import LoyaltyOnboardingPopup from "components/molecules/LoyaltyOnboardingPopup";
import { useBreakpoint } from "hooks/useBreakpoint";
import {
  LOYALTY_ONBOARDING_POPUPS,
  LOYALTY_ONBOARDING_DURATIONS,
  GAP_CARD,
  WIDTH_POPUP,
} from "./constants";
import { useDisabledScroll } from "./hooks";
import { LoyaltyOnboardingPopupPropsWithKey } from "./types";
import "./styles.sass";

type LoyaltyOnboardingProps = {
  isProgress: boolean;
  onCloseOnboard: () => void;
  onEndWatchOnboard: () => void;
};

const LoyaltyOnboardingDesktop: FC<LoyaltyOnboardingProps> = ({
  isProgress,
  onCloseOnboard,
  onEndWatchOnboard,
}) => {
  const { isCustomBreakpointBigger } = useBreakpoint(440);
  const {
    isCustomBreakpointSmaller,
    isCustomBreakpointBigger: isCustomBreakpointBiggerDesktop,
  } = useBreakpoint(1180);
  const isTablet = !!isCustomBreakpointBigger && !!isCustomBreakpointSmaller;
  const [popups, setPopups] = useState<LoyaltyOnboardingPopupPropsWithKey[]>(
    LOYALTY_ONBOARDING_POPUPS,
  );
  const [durations, setDurations] = useState(LOYALTY_ONBOARDING_DURATIONS);
  const [showBanner, setShowBanner] = useState(!isTablet);
  const [widthContainer, setWidthContainer] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const refCarousel = useRef<HTMLDivElement>(null);
  useDisabledScroll(ref, isProgress);

  const calculateLeftOffset = useCallback(
    (index: number) => {
      if (isTablet) {
        const offsetLeft = widthContainer / 2 - WIDTH_POPUP / 2;
        return (
          WIDTH_POPUP +
          GAP_CARD +
          Number(index) * WIDTH_POPUP +
          GAP_CARD * index -
          offsetLeft
        );
      }

      return index * WIDTH_POPUP + GAP_CARD * index;
    },
    [isTablet, widthContainer],
  );

  useEffect(() => {
    if (!refCarousel.current) {
      return;
    }

    setWidthContainer(refCarousel.current.getBoundingClientRect().width);
  }, []);

  useEffect(() => {
    if (refCarousel.current && widthContainer !== 0 && isProgress) {
      refCarousel.current.scrollTo({
        left: calculateLeftOffset(0),
        behavior: "smooth",
      });
    }
  }, [
    calculateLeftOffset,
    refCarousel,
    widthContainer,
    showBanner,
    isProgress,
  ]);

  const onOnboardReload = () => {
    setTimeout(() => {
      setPopups(LOYALTY_ONBOARDING_POPUPS);
      setDurations(LOYALTY_ONBOARDING_DURATIONS);
      setShowBanner(!isTablet);
    }, 1000);
  };

  const onHandlePopup = (index: number) => {
    if (index === popups.length) {
      onEndWatchOnboard();
      onOnboardReload();
      return;
    }

    setPopups(
      popups.map((popup, indexPopup) => {
        return {
          ...popup,
          isActive: indexPopup === index,
        };
      }) as LoyaltyOnboardingPopupPropsWithKey[],
    );

    setDurations(
      durations.map((duration, indexDuration) => {
        return {
          ...duration,
          isFinished: indexDuration < index,
          isProgress: indexDuration === index,
        };
      }),
    );

    const offsetLeft = calculateLeftOffset(index);

    refCarousel?.current?.scrollTo({
      left: offsetLeft,
      behavior: "smooth",
    });
  };

  const className = classNames("LoyaltyOnboarding", {
    LoyaltyOnboarding_active: isProgress,
    LoyaltyOnboarding_fixed:
      isCustomBreakpointBigger && isCustomBreakpointSmaller,
    LoyaltyOnboarding_fixedDesktop: isCustomBreakpointBiggerDesktop,
  });

  const onClose = () => {
    onCloseOnboard();
    onOnboardReload();
  };

  return (
    <div ref={ref} className={className}>
      <div className="LoyaltyOnboarding__popups" ref={refCarousel}>
        <LoyaltyOnboardingPopup isHide />
        {isProgress &&
          popups.map((popup, index) => (
            <LoyaltyOnboardingPopup
              {...popup}
              key={popup.key}
              durations={durations}
              handleNext={() => onHandlePopup(index + 1)}
              isLast={index === popups.length - 1}
              handleClick={() => onHandlePopup(index)}
              handleClickClose={onClose}
              isHide={undefined}
            >
              {popup?.content &&
                popup?.content({
                  inactive: !popup.isActive,
                  onNext: () => onHandlePopup(index + 1),
                })}
            </LoyaltyOnboardingPopup>
          ))}
        <LoyaltyOnboardingPopup isHide />
      </div>
    </div>
  );
};

export default LoyaltyOnboardingDesktop;
